import {
  Checkbox,
  Input,
  Textarea,
  Dropdown,
  Option,
  Spinner,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-components';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import DateRangeSelectorComponent from '../../../commons/component/Inputs/DateRangeSelector/DateRangeSelector.Component';
import { CurrencySelectorComponent } from '../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import ProtocolSelectorComponent from '../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import ContractStagesSelectorComponent from '../../../commons/component/Inputs/ContractStagesSelector/ContractStages.Selector.Component';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import ProtocolPatientSelectorComponent from '../../../commons/component/Inputs/ProtocolPatientSelector/ProtocolPatientSelector.Component';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import ServiceCategorySelectorComponent from '../../../commons/component/Inputs/ServiceCategorySelector/ServiceCategorySelector.Component';
import { ServiceCategory } from '../../../models/Entities/ServiceCategory';
import { Service } from '../../../models/Entities/Service';
import ServiceReceiptsSection from '../ServiceReceipts/ServiceReceiptsSection.Component';
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

////i18Next
import { useTranslation } from 'react-i18next';

import { buildExistingServicePayload, buildNewServicePayload, FormValidation, Props, validateForm, mapStateToProps, mapDispatchToProps } from './ServiceForm.Functions';
import { useServiceFormStyles } from './ServiceForm.Styles';

export const ServiceFormComponent: React.FC<Props> = (props) => {
  const [t] = useTranslation();
  const styles = useServiceFormStyles();

  // Local state
  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [FormValidationStatus, setFormValidationStatus] = useState<FormValidation | undefined>();

  // Connected state slices
  const ServiceState = props.ServiceState;
  const ConsumerState = props.ConsumerState;
  const ServiceCategoryState = props.ServiceCategoryState;
  const MedicalProtocolsState = props.MedicalProtocolsState;
  const ContractState = props.ContractState;

  // entity properties
  const [description, setDescription] = useState<string>();
  const [consumerId, setConsumerId] = useState<string>();
  const [providerId, setProviderId] = useState<string>();
  const [serviceCategoryId, setServiceCategoryId] = useState<ServiceCategory[]>([]);
  const [serviceStatusId, setServiceStatusId] = useState<number>(1);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [originPlace, setOriginPlace] = useState<string>();
  const [destinationPlace, setDestinationPlace] = useState<string>();
  const [estimateCost, setEstimateCost] = useState<string>('0');
  const [finalCost, setFinalCost] = useState<string>('0');
  const [currencyType, setCurrencyType] = useState<string>('USD');
  const [attachReceipt, setAttachReceipt] = useState<boolean>(false);
  const [receivesPerDiem, setReceivesPerDiem] = useState<boolean>(false);
  const [takesBreakfast, setTakesBreakfast] = useState<boolean>(false);
  const [protocolId, setProtocolId] = useState<number | undefined>();
  const [contractId, setContractId] = useState<number | undefined>();
  const [stageId, setStageId] = useState<number | undefined>();
  const [numberInProtocol, setNumberInProtocol] = useState<string[]>([]);

  // Ref to track last processed contractId to avoid loops
  const lastProcessedContractId = useRef<number | undefined>(undefined);

  // Options
  const statusOptions = useMemo(
    () => [
      { id: '1', name: t('serviceForm.status.active') },
      { id: '2', name: t('serviceForm.status.inactive') },
      { id: '3', name: t('serviceForm.status.completed') },
      { id: '4', name: t('serviceForm.status.cancelled') }
    ],
    [t]
  );

  // Derived selections
  const selectedProtocols = useMemo(() => {
    if (protocolId && MedicalProtocolsState?.list) {
      const selectedProtocol = MedicalProtocolsState.list.find((p: Protocol) => p.id === protocolId);
      return selectedProtocol ? [selectedProtocol] : undefined;
    }
    return undefined;
  }, [protocolId, MedicalProtocolsState?.list]);

  // Load consumers and categories on mount
  useEffect(() => {
    if (!ConsumerState?.successLoadingAll && !ConsumerState?.isLoadingAll) {
      props.GetAllConsumers();
    }
    if (!ServiceCategoryState?.successLoadingAll && !ServiceCategoryState?.isLoadingAll) {
      props.GetAllServiceCategories();
    }
  }, [
    ConsumerState?.successLoadingAll,
    ConsumerState?.isLoadingAll,
    ServiceCategoryState?.successLoadingAll,
    ServiceCategoryState?.isLoadingAll,
    props.GetAllConsumers,
    props.GetAllServiceCategories
  ]);

  // Auto-select first consumer when consumers are loaded
  useEffect(() => {
    if (ConsumerState?.successLoadingAll && ConsumerState?.list && ConsumerState.list.length > 0 && !consumerId) {
      setConsumerId(ConsumerState.list[0].id);
    }
  }, [ConsumerState?.successLoadingAll, ConsumerState?.list, consumerId]);

  // effects
  useEffect(() => {
    if (props.Service && props.isOpen) {
      // Reset tracking ref when loading a different service
      lastProcessedContractId.current = undefined;

      setDescription(props.Service.description || '');
      setConsumerId(props.Service.consumerId ?? undefined);
      setProviderId(props.Service.providerId || '');
      setServiceCategoryId(props.Service.serviceCategoryId ? [{ id: props.Service.serviceCategoryId } as ServiceCategory] : []);
      setServiceStatusId(props.Service.serviceStatusId);
      setStartDate(props.Service.startDate ? new Date(props.Service.startDate) : undefined);
      setEndDate(props.Service.endDate ? new Date(props.Service.endDate) : undefined);
      setOriginPlace(props.Service.originPlace || '');
      setDestinationPlace(props.Service.destinationPlace || '');
      setEstimateCost(props.Service.estimateCost.toString());
      setFinalCost(props.Service.finalCost.toString());
      setCurrencyType(props.Service.currencyType || 'USD');
      setAttachReceipt(!!props.Service.attachReceipt);
      setReceivesPerDiem(!!props.Service.receivesPerDiem);
      setTakesBreakfast(!!props.Service.takesBreakfast);
      setProtocolId(props.Service.protocolId);
      setStageId(props.Service.stageId);
      setNumberInProtocol(props.Service.numberInProtocol ? [props.Service.numberInProtocol] : []);

      // Load protocol data if editing
      // First load patients, then contract association to avoid overwriting
      if (props.Service.protocolId) {
        const currentProtocolId = props.Service.protocolId; // Capture value for setTimeout closure

        // Load patients first
        props.GetProtocolPatients(currentProtocolId, '');

        // Then load contract association after a small delay to ensure patients are loaded first
        setTimeout(() => {
          props.GetAllProtocolContractAssociations(currentProtocolId);
        }, 100);
      }
    } else if (!props.isOpen) {
      ResetForm();
    }
  }, [props.Service, props.isOpen, props.GetProtocolPatients, props.GetAllProtocolContractAssociations]);

  // Effects
  useEffect(() => {
    if (
      submittedForm &&
      ServiceState &&
      (ServiceState.isAddingSuccess || ServiceState.isSavingSuccess)
    ) {
      ResetForm();
      props.dismissPanel();
    }
  }, [
    ServiceState?.isAddingSuccess,
    ServiceState?.isSavingSuccess,
    ServiceState,
    submittedForm,
    props
  ]);

  // Effect to update contractId when protocol contract association is loaded
  useEffect(() => {
    if (protocolId && MedicalProtocolsState?.list && props.isOpen) {
      const selectedProtocol = MedicalProtocolsState.list.find((p: Protocol) => p.id === protocolId);

      if (selectedProtocol?.contractAssociation) {
        const newContractId = selectedProtocol.contractAssociation.contractId;
        // Only update if it really changed to avoid loops
        if (lastProcessedContractId.current !== newContractId) {
          lastProcessedContractId.current = newContractId;
          setContractId(newContractId);
        }
      }
      // Note: Don't clear contractId/stageId here when association is missing
      // as it might be loading. Only clear when protocol is explicitly deselected.
    } else if (!protocolId) {
      // Protocol deselected, clear everything
      lastProcessedContractId.current = undefined;
      setContractId(undefined);
    }
  }, [protocolId, MedicalProtocolsState?.list, props.isOpen]);

  // Effect to fetch contract by ID when contractId changes
  useEffect(() => {
    if (contractId && props.isOpen) {
      // Check if contract is already in the list
      const contractExists = ContractState?.list?.find((c: Contract) => c.id === contractId);

      if (!contractExists) {
        // Fetch the contract if not in the list
        props.GetContractById(contractId);
      }
    }
  }, [contractId, ContractState?.list, props.GetContractById, props.isOpen]);

  const ResetForm = () => {
    setDescription('');
    setConsumerId(undefined);
    setProviderId('');
    setServiceCategoryId([]);
    setServiceStatusId(1);
    setStartDate(undefined);
    setEndDate(undefined);
    setOriginPlace('');
    setDestinationPlace('');
    setEstimateCost('0');
    setFinalCost('0');
    setCurrencyType('USD');
    setAttachReceipt(false);
    setReceivesPerDiem(false);
    setTakesBreakfast(false);
    setProtocolId(undefined);
    setContractId(undefined);
    setStageId(undefined);
    setNumberInProtocol([]);
    setSubmittedForm(false);
  };

  // Events
  const onChangeDescription = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(ev.target.value);
  };

  const onChangeOriginPlace = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setOriginPlace(ev.target.value);
  };

  const onChangeDestinationPlace = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setDestinationPlace(ev.target.value);
  };

  const onChangeEstimateCost = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setEstimateCost(ev.target.value || '0');
  };

  const onChangeFinalCost = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setFinalCost(ev.target.value || '0');
  };

  const onFocusNumericInput = (ev: React.FocusEvent<HTMLInputElement>) => {
    ev.target.select();
  };

  const onChangeCurrencyType = useCallback((currency: string) => {
    setCurrencyType(currency || 'USD');
  }, []);

  const onChangeProtocolId = (selectedProtocols: Protocol[] | undefined) => {
    const newProtocolId = selectedProtocols && selectedProtocols.length > 0 ? selectedProtocols[0].id : undefined;
    setProtocolId(newProtocolId);
    setStageId(undefined); // Reset stage when protocol changes
    setNumberInProtocol([]); // Reset patient when protocol changes
    if (newProtocolId) {
      // Load patients first
      props.GetProtocolPatients(newProtocolId, '');
      props.GetAllProtocolContractAssociations(newProtocolId);
    } else {
      setContractId(undefined);
      setStageId(undefined);
      setNumberInProtocol([]);
    }
  };

  const onChangeStageId = (selectedStages: ContractStage[]) => {
    setStageId(selectedStages && selectedStages.length > 0 ? selectedStages[0].id : undefined);
  };

  const onChangeNumberInProtocol = (selectedPatients: ProtocolPatient[]) => {
    setNumberInProtocol(selectedPatients?.map(p => p.numberInProtocol) || []);
  };

  const onCategoryChange = (categories: ServiceCategory[]) => {
    setServiceCategoryId(categories);
  };

  const onStatusChange = (ev: any, data: any) => {
    setServiceStatusId(Number.parseInt(data.optionValue));
  };

  const onDateRangeChange = (startDate: Date | undefined, endDate: Date | undefined) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const runValidation = useCallback(() => {
    const validation = validateForm(
      { description, startDate, endDate, estimateCost, finalCost },
      t
    );

    setFormValidationStatus(validation);
    return validation.success;
  }, [description, startDate, endDate, estimateCost, finalCost, t]);

  const HandleSaveForm = () => {
    setSubmittedForm(true);

    if (runValidation()) {
      // Ensure consumerId is set with a random consumer if not already set
      let finalConsumerId = consumerId;
      if (!finalConsumerId && ConsumerState?.list && ConsumerState.list.length > 0) {
        const randomIndex = Math.floor(Math.random() * ConsumerState.list.length);
        finalConsumerId = ConsumerState.list[randomIndex].id;
      }

      if (props.Service) {
        const payload = buildExistingServicePayload({
          service: props.Service,
          protocolId,
          stageId,
          numberInProtocol,
          consumerId: finalConsumerId!,
          providerId,
          serviceCategoryId,
          description,
          serviceStatusId,
          startDate,
          endDate,
          originPlace,
          destinationPlace,
          estimateCost,
          finalCost,
          currencyType,
          attachReceipt,
          receivesPerDiem,
          takesBreakfast
        });
        props.UpdateService(payload);
      } else {
        const payload = buildNewServicePayload({
          protocolId,
          stageId,
          numberInProtocol,
          consumerId: finalConsumerId!,
          providerId,
          serviceCategoryId,
          description,
          serviceStatusId,
          startDate,
          endDate,
          originPlace,
          destinationPlace,
          estimateCost,
          finalCost,
          currencyType,
          attachReceipt,
          receivesPerDiem,
          takesBreakfast
        });
        props.AddService(payload);
      }
    }
  };

  const HandleCancelForm = () => {
    ResetForm();
    props.dismissPanel();
  };

  const requiredError = (missing: boolean) =>
    submittedForm === true && missing ? t('serviceForm.formFields.requiredField') : undefined;

  const selectedContract = contractId ? ContractState?.list?.find((c: Contract) => c.id === contractId) : undefined;

  const formFields = (
    <GuideDrawerForm testId="service-form">
      <GuideField label={t('serviceForm.formFields.description')} required error={requiredError(!description)}>
        <Textarea
          name="txt_description"
          rows={3}
          resize="vertical"
          onChange={onChangeDescription}
          value={description}
        />
      </GuideField>

      {protocolId && !props.isLoadingAssociatedContracts && !selectedProtocols?.[0]?.contractAssociation && (
        <GuideMessageBar intent="warning" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{t('serviceForm.messages.protocolWithoutContractTitle')}</MessageBarTitle>
            {t('serviceForm.messages.protocolWithoutContractMessage')}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      <GuideField label={t('serviceForm.formFields.protocolId')}>
        <ProtocolSelectorComponent
          key={protocolId || 'new-protocol'}
          selectionLimit={1}
          selectedProtocols={selectedProtocols}
          onChangeSelection={onChangeProtocolId}
        />
      </GuideField>

      <GuideFieldGrid>
        <GuideField label={t('serviceForm.formFields.visit')}>
          {(() => {
            if (contractId && selectedContract) {
              return (
                <ContractStagesSelectorComponent
                  key={`${props.Service?.id || 'new'}-${contractId}-${stageId || 'new-stage'}`}
                  ContractId={contractId}
                  onSelectedContractStage={onChangeStageId}
                  ContractStagesSelected={stageId ? [stageId] : undefined}
                  MultiselectEnable={false}
                  showTitle={false}
                />
              );
            }
            if (contractId) {
              return <Spinner size="tiny" label={t('serviceForm.panel.loadingData')} />;
            }
            return (
              <Input
                disabled
                placeholder={t('serviceForm.formFields.selectProtocolFirst')}
              />
            );
          })()}
        </GuideField>
        <GuideField label={t('serviceForm.formFields.numberInProtocol')}>
          {protocolId ? (
            <ProtocolPatientSelectorComponent
              key={`patient-selector-${props.Service?.id || 'new'}-${protocolId}`}
              ProtocolId={protocolId}
              onSelectedProtocolPatient={onChangeNumberInProtocol}
              ProtocolPatientsSelectedNumber={numberInProtocol}
              MultiselectEnable={false}
              showTitle={false}
            />
          ) : (
            <Input
              disabled
              placeholder={t('serviceForm.formFields.selectProtocolFirst')}
            />
          )}
        </GuideField>
      </GuideFieldGrid>

      {/* Consumer field hidden - auto-assigned to first available consumer */}

      <GuideFieldGrid>
        <ServiceCategorySelectorComponent
          onSelectedServiceCategory={onCategoryChange}
          ServiceCategoriesSelected={serviceCategoryId}
          MultiselectEnable={false}
          showLabel={true}
          showManageButton={true}
        />

        <GuideField label={t('serviceForm.formFields.status')} required>
          <Dropdown
            value={statusOptions.find(s => Number.parseInt(s.id) === serviceStatusId)?.name || ''}
            selectedOptions={[serviceStatusId.toString()]}
            onOptionSelect={onStatusChange}
          >
            {statusOptions.map((status) => (
              <Option key={status.id} value={status.id}>
                {status.name}
              </Option>
            ))}
          </Dropdown>
        </GuideField>
      </GuideFieldGrid>

      <DateRangeSelectorComponent
        startDate={startDate}
        endDate={endDate}
        onDateChange={onDateRangeChange}
        startDateLabel={t('serviceForm.formFields.startDate')}
        endDateLabel={t('serviceForm.formFields.endDate')}
      />

      <GuideFieldGrid>
        <GuideField label={t('serviceForm.formFields.originPlace')}>
          <Input
            name="txt_origin"
            type="text"
            onChange={onChangeOriginPlace}
            value={originPlace}
          />
        </GuideField>
        <GuideField label={t('serviceForm.formFields.destinationPlace')}>
          <Input
            name="txt_destination"
            type="text"
            onChange={onChangeDestinationPlace}
            value={destinationPlace}
          />
        </GuideField>
      </GuideFieldGrid>

      <div className={styles.checkRow}>
        <Checkbox
          checked={attachReceipt}
          onChange={(_event, data) => setAttachReceipt(!!data.checked)}
          label={t('serviceForm.formFields.attachReceipt', 'Adjunta comprobante')}
        />
        <Checkbox
          checked={receivesPerDiem}
          onChange={(_event, data) => setReceivesPerDiem(!!data.checked)}
          label={t('serviceForm.formFields.receivesPerDiem', 'Recibe viático')}
        />
        <Checkbox
          checked={takesBreakfast}
          onChange={(_event, data) => setTakesBreakfast(!!data.checked)}
          label={t('serviceForm.formFields.takesBreakfast', 'Incluye desayuno')}
        />
      </div>

      <GuideFieldGrid>
        <GuideField label={t('serviceForm.formFields.estimateCost')} required error={requiredError(!estimateCost)}>
          <Input
            name="txt_estimateCost"
            type="number"
            onChange={onChangeEstimateCost}
            onFocus={onFocusNumericInput}
            value={estimateCost}
            contentBefore="$"
          />
        </GuideField>
        <GuideField label={t('serviceForm.formFields.finalCost')} required error={requiredError(!finalCost)}>
          <Input
            name="txt_finalCost"
            type="number"
            onChange={onChangeFinalCost}
            onFocus={onFocusNumericInput}
            value={finalCost}
            contentBefore="$"
          />
        </GuideField>
        <CurrencySelectorComponent
          label={t('serviceForm.formFields.currencyType')}
          defaultSelected={props.Service?.currencyType || 'USD'}
          onCurrencyChange={onChangeCurrencyType}
          errorMessage={undefined}
        />
      </GuideFieldGrid>
    </GuideDrawerForm>
  );

  const isSaving = !!(ServiceState?.isAdding || ServiceState?.isSaving);
  const saveError = ServiceState && (ServiceState.failOnAdding || ServiceState.failOnSaving) ? ServiceState.error : undefined;

  return (
    <GuideDrawer
      open={props.isOpen}
      size="medium"
      title={props.Service ? t('serviceForm.panel.titleEdit') : t('serviceForm.panel.title')}
      eyebrow={props.Service ? selectedProtocols?.[0]?.title : undefined}
      onDismiss={props.dismissPanel}
      testId="service-form-drawer"
      secondaryAction={{ label: t('serviceForm.footer.cancelButton'), onClick: HandleCancelForm }}
      primaryAction={{ label: t('serviceForm.footer.saveButton'), onClick: HandleSaveForm, loading: isSaving }}
    >
      {submittedForm && !FormValidationStatus?.success && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{t('serviceForm.panel.reviewInformation')}</MessageBarTitle>
            <ul className={styles.errorList}>
              {FormValidationStatus?.errors.map((errorMessage: string) => (
                <li key={errorMessage}>{errorMessage}</li>
              ))}
            </ul>
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {saveError && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            {saveError.ErrorMessage}
            {saveError.Errors && saveError.Errors.length > 0 && (
              <ul className={styles.errorList}>
                {saveError.Errors.map((item: string, index: number) => <li key={index}>{item}</li>)}
              </ul>
            )}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {(ConsumerState?.isLoadingAll || ServiceCategoryState?.isLoadingAll) && (
        <div className={styles.loadingContainer}>
          <Spinner label={t('serviceForm.panel.loadingData')} />
        </div>
      )}

      {formFields}

      {/* Receipts belong to a saved service: only when editing (contract §10.4) */}
      {props.Service && (
        <ServiceReceiptsSection
          service={
            ServiceState?.list?.find((item: Service) => item.id === props.Service?.id) ?? props.Service
          }
          attachReceipt={attachReceipt}
        />
      )}
    </GuideDrawer>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ServiceFormComponent);
